import React, { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = user
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      // Only log out on a real auth failure, not when the server is unreachable.
      if (e?.response?.status === 401 || e?.response?.status === 403) setUser(false);
      else setUser((u) => (u ? u : false));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    const onExpired = () => setUser(false);
    const onVisible = () => {
      if (document.visibilityState === "visible") api.get("/health").catch(() => {});
    };
    window.addEventListener("session-expired", onExpired);
    document.addEventListener("visibilitychange", onVisible);
    const ping = setInterval(() => {
      if (document.visibilityState === "visible") api.get("/health").catch(() => {});
    }, 4 * 60 * 1000);
    return () => {
      window.removeEventListener("session-expired", onExpired);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(ping);
    };
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.token) localStorage.setItem("lpg_token", data.token);
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("lpg_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}
